import * as React from 'react';
import { useLocation } from 'react-router-dom';

import { getAvailableRoutes, getNavigationRoutes } from '@/app/route-registry';
import { useSession } from '@/components/auth/user-provider';
import { navigationFeatureState } from '@/features/tenancy/feature-ui';
import { tenantPath, tenantSlugFromPath } from '@/features/tenancy/urls';
import { useEntityFeatures } from '@/hooks/use-entity-features';
import { usePluginStatus } from '@/hooks/use-plugin-status';

/** Resolves the route registry against plugin, role, and capability state. */
export function useBaseAppRoutes() {
  const { plugins } = usePluginStatus();
  const { session } = useSession();
  const role = session.status === 'signedIn' ? session.user.role : undefined;
  const capabilities = session.status === 'signedIn' ? session.user.capabilities : undefined;

  return React.useMemo(
    () => getAvailableRoutes(plugins, role, capabilities),
    [plugins, role, capabilities]
  );
}

export function useAppRoutes() {
  const routes = useBaseAppRoutes();
  const { pathname } = useLocation();
  const tenantSlug = tenantSlugFromPath(pathname);
  const featureQuery = useEntityFeatures();
  const currentFeatures = navigationFeatureState(featureQuery);

  return React.useMemo(() => {
    const visibleRoutes = getNavigationRoutes(routes, currentFeatures);
    return tenantSlug
      ? visibleRoutes.map((route) => ({ ...route, path: tenantPath(tenantSlug, route.path) }))
      : visibleRoutes;
  }, [currentFeatures, routes, tenantSlug]);
}
